import { Mail, TrashIcon } from 'lucide-react';
import React, { useState } from 'react';

const styles = {
    page: {
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e8edf5 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
        padding: '24px',
    },
    card: {
        background: '#ffffff',
        borderRadius: '20px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.10)',
        padding: '52px 44px 44px',
        width: '100%',
        maxWidth: '440px',
        textAlign: 'center',
        transition: 'all 0.4s ease',
    },
    iconWrapper: {
        width: '72px',
        height: '72px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #ff4e50, #f9404b)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 24px',
        boxShadow: '0 8px 24px rgba(255,78,80,0.30)',
    },
    trashIcon: {
        fontSize: '32px',
        color: '#fff',
    },
    title: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#1a1a2e',
        marginBottom: '8px',
    },
    subtitle: {
        fontSize: '14px',
        color: '#6b7280',
        lineHeight: '1.6',
        marginBottom: '32px',
    },
    inputGroup: {
        textAlign: 'left',
        marginBottom: '20px',
    },
    label: {
        display: 'block',
        fontSize: '13px',
        fontWeight: '600',
        color: '#374151',
        marginBottom: '8px',
        letterSpacing: '0.02em',
    },
    inputWrapper: {
        position: 'relative',
    },
    input: {
        width: '100%',
        padding: '13px 16px',
        border: '1.5px solid #e5e7eb',
        borderRadius: '12px',
        fontSize: '15px',
        color: '#111827',
        outline: 'none',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        boxSizing: 'border-box',
        background: '#f9fafb',
    },
    button: {
        width: '100%',
        padding: '14px',
        background: 'linear-gradient(135deg, #ff4e50, #f9404b)',
        color: '#fff',
        border: 'none',
        borderRadius: '12px',
        fontSize: '16px',
        fontWeight: '600',
        cursor: 'pointer',
        letterSpacing: '0.03em',
        transition: 'opacity 0.2s, transform 0.1s',
        marginTop: '8px',
        boxShadow: '0 6px 20px rgba(255,78,80,0.30)',
    },
    /* ── Success state ── */
    successWrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        animation: 'fadeInUp 0.5s ease',
    },
    successCircle: {
        width: '90px',
        height: '90px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #22c55e, #16a34a)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 28px',
        boxShadow: '0 8px 28px rgba(34,197,94,0.35)',
        animation: 'popIn 0.45s cubic-bezier(0.175,0.885,0.32,1.275)',
    },
    checkIcon: {
        fontSize: '46px',
        color: '#fff',
        lineHeight: 1,
    },
    successTitle: {
        fontSize: '22px',
        fontWeight: '700',
        color: '#1a1a2e',
        marginBottom: '10px',
    },
    successMsg: {
        fontSize: '14px',
        color: '#6b7280',
        lineHeight: '1.7',
        maxWidth: '340px',
    },
    emailBadge: {
        marginTop: '18px',
        padding: '10px 20px',
        background: '#f0fdf4',
        border: '1.5px solid #bbf7d0',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#16a34a',
        wordBreak: 'break-all',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
};

/* Inject keyframe animations once */
const injectStyles = () => {
    if (document.getElementById('dua-keyframes')) return;
    const el = document.createElement('style');
    el.id = 'dua-keyframes';
    el.textContent = `
    @keyframes popIn {
      0%   { transform: scale(0); opacity: 0; }
      100% { transform: scale(1); opacity: 1; }
    }
    @keyframes fadeInUp {
      0%   { opacity: 0; transform: translateY(20px); }
      100% { opacity: 1; transform: translateY(0); }
    }
    .dua-input:focus {
      border-color: #ff4e50 !important;
      box-shadow: 0 0 0 3px rgba(255,78,80,0.12) !important;
      background: #fff !important;
    }
    .dua-btn:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
    .dua-btn:active:not(:disabled) { transform: translateY(0px); }
    .dua-btn:disabled { opacity: 0.55; cursor: not-allowed; }
  `;
    document.head.appendChild(el);
};

const DeleteUserAccount = () => {
    injectStyles();

    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    const validateEmail = (val) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!validateEmail(email)) {
            setError('Please enter a valid email address.');
            return;
        }

        setLoading(true);
        /* Simulate API call — replace with real request */
        await new Promise((r) => setTimeout(r, 1500));
        setLoading(false);
        setSubmitted(true);
    };

    return (
        <div style={styles.page}>
            <div style={styles.card}>
                {!submitted ? (
                    <>
                        {/* Header icon */}
                        <div style={styles.iconWrapper}>
                            <span style={styles.trashIcon}><TrashIcon /></span>
                        </div>

                        <h1 style={styles.title}>Delete Your Account</h1>
                        <p style={styles.subtitle}>
                            Enter your registered email address below. Your account and all
                            associated data will be permanently deleted.
                        </p>

                        <form onSubmit={handleSubmit}>
                            <div style={styles.inputGroup}>
                                <label style={styles.label} htmlFor="dua-email">
                                    Email Address
                                </label>
                                <div style={styles.inputWrapper}>
                                    <input
                                        id="dua-email"
                                        className="dua-input"
                                        type="email"
                                        placeholder="you@example.com"
                                        value={email}
                                        onChange={(e) => { setEmail(e.target.value); setError(''); }}
                                        style={styles.input}
                                        autoComplete="email"
                                        disabled={loading}
                                    />
                                </div>
                                {error && (
                                    <p style={{ color: '#ef4444', fontSize: '13px', marginTop: '6px', textAlign: 'left' }}>
                                        {error}
                                    </p>
                                )}
                            </div>

                            <button
                                type="submit"
                                className="dua-btn"
                                style={styles.button}
                                disabled={loading || !email.trim()}
                            >
                                {loading ? 'Processing…' : 'Delete My Account'}
                            </button>
                        </form>
                    </>
                ) : (
                    /* ── Success state ── */
                    <div style={styles.successWrapper}>
                        <div style={styles.successCircle}>
                            <span style={styles.checkIcon}>✓</span>
                        </div>
                        <h2 style={styles.successTitle}>Check Your Email</h2>
                        <p style={styles.successMsg}>
                            We have sent a confirmation link to your email address.
                            Open your email and click the <strong>Delete Account</strong> option
                            to permanently delete your account and all associated data.
                        </p>
                        <div style={styles.emailBadge}><Mail size={16} /> {email}</div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DeleteUserAccount;